import { TFunction } from 'react-i18next'
import * as yup from 'yup'
import { AnyObject } from 'yup/lib/types'
import {
  BeneficiaryDetailsCurrency,
  BeneficiaryTransferDestination,
  USDBeneficiaryOptions,
} from 'types/beneficiaries'

const MIN_LENGTH = 2
const MAX_LENGTH = 200
const ADDRESS_FIELDS_REQUIRED_TOGETHER = ['addressLine1', 'city', 'postCode', 'region']

interface FieldValidationProps {
  value: string
  ctx: yup.TestContext<AnyObject>
  label: string
  t: TFunction<'translation', undefined>
  minLength?: number
  maxLength?: number
}

interface CreateBeneficiaryAddressSchemaProps {
  label: string
  t: TFunction<'translation', undefined>
  minLength?: number
  maxLength?: number
}

const isValidLength = ({ value, ctx, label, t, minLength, maxLength }: FieldValidationProps) => {
  const minimumLength = minLength ?? MIN_LENGTH
  const maximumLength = maxLength ?? MAX_LENGTH

  if (value?.length < minimumLength) {
    return ctx.createError({
      message: t?.('fieldMinLength', {
        field: label,
        min: MIN_LENGTH,
        interpolation: { escapeValue: false },
      }),
    })
  }

  if (value?.length > maximumLength) {
    return ctx.createError({
      message: t?.('fieldMaxLength', {
        field: label,
        min: MAX_LENGTH,
        interpolation: { escapeValue: false },
      }),
    })
  }

  return true
}

const validateAddressField = ({
  value,
  ctx,
  label,
  t,
  minLength,
  maxLength,
}: FieldValidationProps) => {
  if (ctx.path === 'addressLine2') {
    return true
  }

  if (ctx?.parent?.transferDestination === BeneficiaryTransferDestination.INTERNATIONAL) {
    if (!value) {
      ctx.schema.required()
      return ctx.createError({
        message: t?.('fieldIsARequiredField', {
          field: label,
          interpolation: { escapeValue: false },
        }),
      })
    }

    return isValidLength({ value, ctx, label, t, minLength, maxLength })
  }

  const currency = ctx?.parent?.currencyCode
  const isUsdAchOrFedwire =
    currency === BeneficiaryDetailsCurrency.USD &&
    ctx?.parent?.beneficiaryPaymentMethod !== USDBeneficiaryOptions.CUBIX

  if (isUsdAchOrFedwire) {
    if (!value) {
      ctx.schema.required()
      return ctx.createError({
        message: t?.('fieldIsARequiredField', {
          field: label,
          interpolation: { escapeValue: false },
        }),
      })
    }

    return isValidLength({ value, ctx, label, t, minLength, maxLength })
  }

  if (!value) {
    const otherFields = ADDRESS_FIELDS_REQUIRED_TOGETHER?.filter(field => field !== ctx.path)
    const isRequired = otherFields?.some(addressField => !!ctx?.parent?.[addressField])

    if (isRequired) {
      ctx.schema.required()
      return ctx.createError({
        message: t?.('partialAddressError'),
      })
    }

    ctx.schema.notRequired()
    return true
  }

  return isValidLength({ value, ctx, label, t, minLength, maxLength })
}

export const createBeneficiaryAddressSchema = ({
  label,
  t,
  minLength,
  maxLength,
}: CreateBeneficiaryAddressSchemaProps) =>
  yup
    .string()
    .trim()
    .label(label)
    .test({
      test(value, ctx) {
        return validateAddressField({ value, ctx, label, t, minLength, maxLength })
      },
    })

export const createBankAddressSchema = (label: string, t: TFunction<'translation', undefined>) =>
  yup
    .string()
    .trim()
    .label(label)
    .min(
      MIN_LENGTH,
      t('fieldMinLength', {
        field: label,
        min: MIN_LENGTH,
        interpolation: { escapeValue: false },
      })
    )
    .max(
      MAX_LENGTH,
      t('fieldMaxLength', {
        field: label,
        max: MAX_LENGTH,
      }).trim()
    )
